import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { useLanguage } from "@/i18n/LanguageContext";
import BLOG_FALLBACK from "@/data/blogFallback";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const { t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/blog`)
      .then((r) => r.json())
      .then((data) => setArticles(data && data.length ? data : BLOG_FALLBACK))
      .catch(() => setArticles(BLOG_FALLBACK))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-void text-white font-body min-h-screen">
      <Seo title={t.seo.blog.title} description={t.seo.blog.desc} path="/blog" />
      <div className="grain-overlay" />
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20">
        <a href="/" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-sm text-neon-cyan hover:gap-3 transition-all mb-10">
          <ArrowLeft size={16} /> Home
        </a>
        <p className="hud-label text-xs uppercase text-neon-cyan mb-4">Blog</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-4" data-testid="blog-title">
          Guide e <span className="neon-text">approfondimenti</span>
        </h1>
        <p className="text-white/60 mb-14 max-w-2xl">AI, gestionali su misura e progettazione impianti: quello che serve sapere per portare la tua azienda nel futuro.</p>

        {loading && <p className="text-white/40 text-sm" data-testid="blog-loading">Caricamento articoli...</p>}

        {!loading && articles.length === 0 && (
          <p className="text-white/40 text-sm" data-testid="blog-empty">I primi articoli sono in arrivo.</p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {articles.map((a, i) => (
            <motion.a
              key={a.id}
              href={`/blog/${a.slug}`}
              data-testid={`blog-card-${a.slug}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="glass-panel spotlight overflow-hidden rounded-2xl hover:-translate-y-1 transition-all duration-500 group flex flex-col"
            >
              {a.image && (
                <img
                  src={a.image}
                  alt={`Copertina articolo: ${a.title}`}
                  loading="lazy"
                  className="w-full h-44 object-cover rounded-t-2xl -mt-0 mb-0"
                />
              )}
              <div className="p-8 flex flex-col flex-1">
              <div className="flex flex-wrap gap-2 mb-4">
                {a.tags?.map((t) => (
                  <span key={t} className="text-[10px] hud-label uppercase border border-neon-cyan/30 text-neon-cyan rounded-full px-3 py-1">{t}</span>
                ))}
              </div>
              <h2 className="font-display text-xl font-bold leading-snug mb-3 group-hover:text-neon-cyan transition-colors">{a.title}</h2>
              <p className="text-sm text-white/60 leading-relaxed mb-6 flex-1">{a.excerpt}</p>
              <span className="flex items-center gap-2 text-sm font-semibold text-neon-cyan">
                Leggi <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </span>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Blog;
